import { createContext, CSSProperties, ReactNode, useContext } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { useLabelContext } from '@/design_system/Label/Label';
import { createBEMClasses } from '@/utils/classname';

import { RadioShape } from '../shared/RadioShape/RadioShape';

import './RadioItem.css';

const { block, element } = createBEMClasses('radio__item');

interface RadioItemGroupContext {
  name: string;
  value: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
  isInvalid?: boolean;
  theme?: 'default' | 'brand';
}

const RadioItemGroupContext = createContext<RadioItemGroupContext>({
  name: '',
  value: null,
  onChange: () => {},
});

export function RadioItem<T extends string>({
  value,
  ariaLabel,
  disabled,
  isInvalid,
  children,
}: {
  value: T;
  ariaLabel: string;
  disabled?: boolean;
  isInvalid?: boolean;
  children?: ReactNode;
}) {
  const {
    name,
    value: groupValue,
    onChange,
    disabled: groupDisabled,
    isInvalid: groupIsInvalid,
    theme,
  } = useContext(RadioItemGroupContext);

  const isChecked = value === groupValue;
  const isDisabled = disabled || groupDisabled;
  const invalid = isInvalid || groupIsInvalid;

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className={block({ checked: isChecked, disabled: isDisabled, invalid })}
        onClick={() => {
          if (!isDisabled) {
            onChange(value);
          }
        }}
      >
        <input
          type="radio"
          name={name}
          value={value}
          checked={isChecked}
          aria-label={ariaLabel}
          onChange={() => onChange(value)}
          disabled={isDisabled}
        />
        <div className={element('content')}>
          <RadioShape checked={isChecked} disabled={isDisabled} isInvalid={invalid} theme={theme} />
          <div className={element('label', { disabled: isDisabled })}>{children}</div>
        </div>
      </div>
    </>
  );
}

export interface RadioItemGroupProps<T extends string> {
  name: string;
  ariaLabel?: string;
  value?: T | null;
  onChange?: (value: T) => void;
  disabled?: boolean;
  isInvalid?: boolean;
  direction?: 'row' | 'column';
  alignment?: 'start' | 'end';
  theme?: 'default' | 'brand';
  children?: ReactNode;
  style?: CSSProperties;
}

export function RadioItemGroup<T extends string>({
  name,
  ariaLabel,
  value = null,
  onChange = () => {},
  disabled,
  isInvalid,
  direction = 'row',
  alignment = 'start',
  theme = 'default',
  children,
  style,
}: RadioItemGroupProps<T>) {
  /**
   * Radio inputs cannot have a single html label for all radio inputs.
   * Therefore, we don't use the `id` here, and use the label as aria-label to be selectable by Playwright.
   */
  const { label } = useLabelContext();

  return (
    <div
      className={element('group', { direction, alignment, theme })}
      style={style}
      aria-label={ariaLabel ?? label}
    >
      <RadioItemGroupContext
        value={{
          name,
          value,
          onChange: onChange as (value: string) => void,
          disabled,
          isInvalid,
          theme,
        }}
      >
        {children}
      </RadioItemGroupContext>
    </div>
  );
}

export const RadioItemYesNo = (props: Omit<RadioItemGroupProps<'yes' | 'no'>, 'children'>) => {
  const { t } = useLingui();

  return (
    <RadioItemGroup {...props}>
      <RadioItem
        value="yes"
        ariaLabel={t({
          id: 'design-system.radio.yes',
          message: 'Yes',
        })}
      >
        <Trans id="design-system.radio.yes">Yes</Trans>
      </RadioItem>
      <RadioItem
        value="no"
        ariaLabel={t({
          id: 'design-system.radio.no',
          message: 'No',
        })}
      >
        <Trans id="design-system.radio.no">No</Trans>
      </RadioItem>
    </RadioItemGroup>
  );
};
